import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import {
  Observable,
  Subscription,
  catchError,
  concatMap,
  from,
  map,
  of,
  retry,
  toArray,
} from 'rxjs';
import { MenuItem } from 'src/app/core/interfaces/misc/menu-item';
import { SyncStatus } from 'src/app/core/interfaces/offline/sync-status';
import { ApiService } from 'src/app/core/services/api/api.service';
import { AuthenticationService } from 'src/app/core/services/authentication/authentication.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { IdbService } from 'src/app/core/services/idb/idb.service';
import { LocalStorageService } from 'src/app/core/services/local-storage/local-storage.service';
import { OfflineService } from 'src/app/core/services/offline/offline.service';
import { RoleService } from 'src/app/core/services/role/role.service';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import { SyncService } from 'src/app/core/services/sync/sync.service';
import { environment } from 'src/environments/environment';
import { LogoutDialogComponent } from '../sidenav/components/logout-dialog/logout-dialog.component';
import { SyncDialogComponent } from '../sync/components/sync-dialog/sync-dialog.component';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import * as Sentry from '@sentry/angular';

@Component({
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss'],
})
export class NavComponent implements OnInit {
  isAuthenticated: boolean;
  currentPath: string;
  syncStatus: SyncStatus;
  connectionStatus: boolean;

  userRole: string;
  // alternateRole: string;

  showCalendarButton: boolean;

  statementCount: number;
  variableConfigStatementCount: number;
  statementsMissing: boolean;

  currentUserEmail: string;
  currentUserRole: string;
  currentUserCompanyCode: string;

  companyCodes: string[];

  apiUrl = environment.apiUrl;

  @Input() sidenavReference2: MatSidenav;

  menuItems: MenuItem[] = [
    {
      label: 'Calendar',
      showOnMobile: true,
      showOnTablet: true,
      showOnDesktop: true,
      route: 'calendar',
      hasSubMenu: true,
    },
    {
      label: 'Admin',
      showOnMobile: false,
      showOnTablet: false,
      showOnDesktop: false,
      route: 'admin/users',
      hasSubMenu: false,
    },
  ];

  calendarAllowedRoles: string[] = [
    'Admin',
    'Super Admin',
    'Foreman',
    'Estimator',
    'Superintendent',
    'Production Scheduling',
    'Production Manager',
    'Service Manager',
    'Service Tech',
    'Service Scheduler',
    'Office Staff',
    'Customer Care Liaison',
    'Customer Service Admin',
    'Service Backup',
    'Sales Manager',
    'Inside Sales',
    'Inside Sales Manager',
    'ACR',
    'Warehouse',
    'Service Scheduling',
    'Finance',
  ];

  mailingRequestsCount: number;

  authStatusSubscription: Subscription;
  pathSubscription: Subscription;
  connectionStatusSubscription: Subscription;

  constructor(
    private authService: AuthenticationService,
    private router: Router,
    private data: DataService,
    private idb: IdbService,
    private role: RoleService,
    public offline: OfflineService,
    private snackbar: SnackbarService,
    private dialog: MatDialog,
    private localStorage: LocalStorageService,
    private api: ApiService,
    private syncService: SyncService,
    private dialogService: DialogService
  ) {}

  ngOnInit(): void {
    // Initialize Auth and User
    this.authStatusSubscription = this.authService.currentAuthStatus.subscribe(
      (authStatus) => {
        if (authStatus) {
          this.isAuthenticated = true;
          this.getCurrentUserRole();
          setTimeout(() => {
            this.getCurrentUserRole();
          }, 3000);
        }
      }
    );

    // Keep track of the current path
    this.pathSubscription = this.data.currentPath.subscribe((path) => {
      this.currentPath = path;
      if (!this.currentPath.includes('login')) {
        this.showCalendarButton = this.determineShowCalendar();
        this.getMailingRequestsCount();
      }
    });

    // Keep track of the user's connection status
    this.connectionStatusSubscription =
      this.offline.currentConnectionStatus.subscribe((connectionStatus) => {
        this.connectionStatus = connectionStatus;
      });

    // Periodically check for missing statements for Estimators
    if (this.userRole === 'Estimator') {
      this.checkForMissingStatements();
      setInterval(() => {
        this.checkForMissingStatements();
      }, 300000);
    }
  }

  getMailingRequestsCount() {
    this.api.getMailingRequestsCount().subscribe({
      next: (count) => {
        this.mailingRequestsCount = count;
      },
      error: (error) => {
        console.error('Error fetching mailing requests count:', error);
        this.mailingRequestsCount = 0; // Fallback to 0 if there's an error
      },
    });
  }

  getUserData() {
    this.currentUserEmail = this.localStorage.get('email');
    this.currentUserRole = this.localStorage.get('role');
    this.currentUserCompanyCode = this.localStorage.get('companyCode');
  }

  ngAfterViewInit() {
    this.showCalendarButton = this.determineShowCalendar();
  }

  determineShowCalendar(): boolean {
    if (
      (this.currentPath.includes('calendar') &&
        this.calendarAllowedRoles.includes(this.userRole)) ||
      (this.currentPath.includes('dashboard') &&
        this.calendarAllowedRoles.includes(this.userRole)) ||
      (this.currentPath.includes('followUps') &&
        this.calendarAllowedRoles.includes(this.userRole))
    ) {
      return true;
    } else {
      return false;
    }
  }

  getCurrentOfflineStatus(): boolean {
    return this.role.getCurrentOfflineStatus();
  }

  getCurrentUserRole() {
    this.userRole = this.role.getCurrentUserRole();
    if (this.userRole && this.currentPath) {
      this.showCalendarButton = this.determineShowCalendar();
      this.getUserData();
    }

    console.log('getCurrentUserRole:', this.userRole);
    if (!this.role.getCurrentOfflineStatus()) {
      console.log('Fetching company codes...');
      this.api.getAllCompanyCodes().subscribe({
        next: (companyCodes) => {
          console.log('Fetched company codes:', companyCodes);
          this.companyCodes = companyCodes;
        },
        error: (error) => {
          console.error('Error fetching company codes:', error);
        },
      });
    }
  }

  goToDashboard() {
    this.router.navigate(['/dashboard']);
  }

  openSyncDialog() {
    if (!this.dialogService.isSyncDialogOpen()) {
      this.dialogService.markSyncDialogOpen();

      const dialogRef = this.dialog.open(SyncDialogComponent, {
        width: '50%',
        data: { userRole: this.userRole, initialSync: false },
      });

      dialogRef.afterClosed().subscribe(() => {
        this.dialogService.markSyncDialogClosed();
        this.syncService.reloadSyncStatus();
        setTimeout(() => {
          // Navigate to dashboard after sync
          this.router.navigate(['/dashboard']);
        }, 1000);
      });
    }
  }

  logout() {
    if (this.role.getCurrentOfflineStatus() === true) {
      this.idb.getAllModifiedJobAddresses().subscribe({
        next: (jobAddresses) => {
          if (
            (jobAddresses.length > 0 &&
              environment.apiUrl !== 'https://accutrac.net/api/') &&
            environment.apiUrl !== 'https://cncdata.net/api/'
          ) {
            this.snackbar.openErrorSnackBar(
              'You have data that needs to be syncronized. Please syncronize before logging out.'
            );
            return;
          } else {
            const dialogRef = this.dialog.open(LogoutDialogComponent);

            dialogRef.afterClosed().subscribe((result: boolean) => {
              if (result) {
                this.authService
                  .logout()
                  .then(() => {
                    this.router.navigate(['auth/login']);
                    this.sidenavReference2.close();
                    // refresh after 1 second
                    setTimeout(() => {
                      window.location.reload();
                    }, 1000);
                  })
                  .catch((e) => console.error(e.message));
              }
            });
          }
        },
      });
    } else {
      this.authService
        .logout()
        .then(() => {
          this.router.navigate(['auth/login']);
          this.sidenavReference2.close();
          // refresh after 1 second
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        })
        .catch((e) => console.error(e.message));
    }
  }

  checkForMissingStatements() {
    this.idb.getAllStatements().subscribe({
      next: (statements) => {
        let intendedStatementCount = Number(
          this.localStorage.get('statement_Count')
        );
        this.statementCount = statements.length || 0;
        if (this.statementCount < intendedStatementCount) {
          this.statementsMissing = true;
        } else {
          this.statementsMissing = false;
        }
      },
    });
  }

  triggerReloadStatements() {
    if (this.connectionStatus) {
      this.snackbar.openSnackBar('Reloading statements from server...');
      this.statementsMissing = false;
      this.idb
        .clearStatements()
        .pipe(concatMap(() => this.reloadStatementsFromAPI()))
        .subscribe({
          error: (e) => {
            console.error(e);
          },
        });
    } else {
      this.snackbar.openErrorSnackBar(
        "You're offline. Please connect to the internet to reload statements."
      );
    }
  }

  reloadStatementsFromAPI(): Observable<void> {
    let userId = this.localStorage.get('userId');
    const statements = this.api.getPWAStatements(userId, 'Sales');

    return statements.pipe(
      map((statements) => {
        return statements.forEach((statement) => {
          return this.idb.addStatement(statement);
        });
      })
    );
  }

  openBugReportWindow() {
    window.open(
      'https://forms.monday.com/forms/4d2b7ddac524658bc4a9f45f7519eb73?r=use1',
      '_blank'
    );
  }

  onCompanyCodeSelected(companyCode: string) {
    // Check if the user is trying to select the current company code
    if (companyCode === this.currentUserCompanyCode) {
      this.snackbar.openSnackBar('You are already using company code: ' + companyCode);
      return;
    }

    // When a company code is selected, save it to the local storage, then reload the page
    this.localStorage.store('companyCode', companyCode);
    Sentry.setTag('companyCode', companyCode);
    window.location.reload();
  }

  ngOnDestroy() {
    this.authStatusSubscription.unsubscribe();
    this.pathSubscription.unsubscribe();
    this.connectionStatusSubscription.unsubscribe();
  }
}
